import React, { useCallback, useMemo, useState } from "react";
import "../../pages/Dashboard.scss";
import { Agent } from "../FeaturedAgents";
import { RootState, useAppDispatch } from "../../store";
import { useSelector } from "react-redux";
import { IconButton } from "@mui/material";
import { v4 as uuid } from "uuid";
import { Agents } from "../../types/master";
import { getCategoryIcon } from "../../utils/agentCategoryIcons";

type MyAgentsProps = {
  agents: Agent[];
  onAgentClick: (agentId: number) => void;
};

export default function MyAgents({
  agents,
  onAgentClick,
}: MyAgentsProps): JSX.Element {
  const dispatch = useAppDispatch();
  const allAgents = useSelector((state: RootState) => state.agents.data);

  // Memoize accessible agents to avoid recalculating on every render
  const accessibleAgents = useMemo(() => {
    return allAgents
      .filter((agent) => agent.is_access === 1 && agent.id !== 501)
      .slice()
      .sort((a, b) => {
        // Ensure agent names are strings and trim whitespace
        const nameA = (a.agent_name || "").trim().toLowerCase();
        const nameB = (b.agent_name || "").trim().toLowerCase();

        // Sort alphabetically in ascending order (A-Z)
        return nameA.localeCompare(nameB, undefined, {
          sensitivity: "base",
          numeric: true,
        });
      });
  }, [allAgents]);

  const handleAgentClick = useCallback(
    (agent: Agents) => {
      onAgentClick(agent.id);
    },
    [onAgentClick],
  );

  // Helper function to check if agent is available for launch
  const isAgentLaunchable = useCallback((agent: Agents): boolean => {
    // Agent IDs that should open using their endpoint URL in a new tab
    // const EXTERNAL_ENDPOINT_AGENTS = [19, 31, 32, 33, 34];
    const EXTERNAL_ENDPOINT_AGENTS = [
      19, 31, 32, 33, 34, 27, 36, 49, 51, 50, 53, 56,
    ];

    // If agent requires external endpoint but doesn't have one, it's not launchable
    if (EXTERNAL_ENDPOINT_AGENTS.includes(agent.id) && !agent.endpoint) {
      return false;
    }

    return true;
  }, []);

  return (
    <>
      <div className="card my-agents">
        <div className="card-header">
          <h3 style={{ fontFamily: "G42 Sans Black Regular" }}>My Agents</h3>
        </div>
        <p className="subtle">
          Pin your frequent workflow to get started faster
        </p>

        {accessibleAgents.length === 0 ? (
          <div className="empty-state">
            <p className="empty-state-message">
              Currently, you do not have rights to access this agent. Current
              version access provided to select SMEs.
            </p>
          </div>
        ) : (
          <div className="tiles">
            {accessibleAgents.map((agent, index) => (
              <div
                key={agent.id}
                className="tile"
                onClick={() =>
                  isAgentLaunchable(agent) && handleAgentClick(agent)
                }
                style={{
                  opacity: !isAgentLaunchable(agent) ? 0.5 : 1,
                  cursor: !isAgentLaunchable(agent) ? "not-allowed" : "pointer",
                }}
              >
                <div className="tile-icon">
                  <img
                    src={getCategoryIcon(agent.category)}
                    alt={`${agent.agent_name} icon`}
                  />
                </div>
                <div className="tile-content">
                  <div className="tile-title">{agent.agent_name}</div>
                  <div className="tile-sub">{agent.category}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
